// src/pages/Login.tsx
import { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@cabukaka/services';
import { useToken } from '@cabukaka/hooks';

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { saveToken } = useToken()
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setErrors({});

    const { email, password } = formData;
    const newErrors: { [key: string]: string } = {};

    if (!email) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Email invalide';
    }

    if (!password) {
      newErrors.password = 'Le mot de passe est requis';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const response = await authService.login({ email, password })

      const token = response.token
      if (token) {
        saveToken(token)
        navigate(`/redirect?token=${token}`);

      }
    } catch (err) {
      setLoading(false);
      setError('Erreur lors de la connexion');
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);
    try {
      const res = await authService.loginWithGoogle({ role: 'shop' })

      if (res.token && res.user) {
        saveToken(res.token)

        navigate(`/redirect?token=${res.token}`)
      }

    } catch (err) {
      setGoogleLoading(false);
      setError('Erreur lors de la connexion avec Google');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (error) setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/30 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-12 items-center">

        {/* Section Gauche - Présentation */}
        <div className="hidden lg:block space-y-8">
          <a href="/" className="flex items-center mb-8">
            <img src="/images/logo.png" className="h-12 w-auto" alt="Cabukaka" />
          </a>

          <div className="space-y-6">
            <h1 className="text-4xl font-bold text-gray-900 leading-tight">
              Bienvenue sur
              <span className="block bg-gradient-to-r from-orange-600 to-orange-400 bg-clip-text text-transparent">
                Cabukaka
              </span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              La plateforme de marketplace qui connecte les vendeurs et acheteurs au Cameroun.
              Gérez vos ventes, vos produits et vos commandes depuis un seul tableau de bord.
            </p>
          </div>

          <div className="space-y-4 mt-8">
            {[
              "Gestion simplifiée de vos produits",
              "Commandes centralisées",
              "Statistiques en temps réel",
              "Support dédié 7j/7",
              "Paiements sécurisés",
              "Connexion rapide avec Google"
            ].map((feature, index) => (
              <div key={index} className="flex items-center space-x-3 group">
                <div className="w-6 h-6 bg-orange-500/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <CheckCircle2 className="w-3 h-3 text-orange-600" />
                </div>
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Section Droite - Formulaire */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 p-8 lg:p-10">
          {/* Header Mobile */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/images/logo.png" className="h-10 w-auto" alt="Cabukaka" />
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Connexion
            </h2>
            <p className="text-gray-600">
              Accédez à votre espace vendeur
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {/* Bouton Google */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading}
            className="w-full flex items-center justify-center space-x-3 px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all mb-6"
          >
            {googleLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-600" />
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span className="text-gray-700 font-medium">Continuer avec Google</span>
              </>
            )}
          </button>

          <div className="flex items-center my-6">
            <div className="flex-grow border-t border-gray-300" />
            <span className="mx-4 text-gray-500 text-sm">OU</span>
            <div className="flex-grow border-t border-gray-300" />
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="vendeur@cabukaka.com"
                  disabled={loading || googleLoading}
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Mot de passe
                </label>
                <a href="/forgot-password" className="text-sm text-orange-600 hover:underline">
                  Mot de passe oublié ?
                </a>
              </div>
              <div className="relative">
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all ${errors.password ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="Votre mot de passe"
                  disabled={loading || googleLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">Se souvenir de moi</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <span>Se connecter</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-gray-600 mt-6">
            Pas encore de compte vendeur ?{' '}
            <a href="/register" className="text-orange-600 font-semibold hover:underline">
              Créer un compte
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}